import { useCallback, useEffect } from 'react';

import { SxProps, Theme } from '@mui/material';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { PermissionItem, PermissionsModel } from '../Permissions.types';
import AddNewPermissionCard from './AddNewPermissionCard';
import PermissionsCard from './PermissionsCard';

interface PermissionsWrapperProps {
  prefix: 'permissions' | `permissions.${number}.childPermissions`;
  sx?: SxProps<Theme>;
}

const PermissionsWrapper = ({ prefix, sx }: PermissionsWrapperProps) => {
  const { control, watch } = useFormContext<PermissionsModel>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: prefix,
  });
  const watchFields = watch(prefix);
  const updateChildPermissions = useCallback((permission: PermissionItem): any => {
    const { resourceType, resourceValue } = permission;
    if (permission?.childPermissions.length > 0) {
      return permission.childPermissions.map(childPermission => ({
        ...permission,
        childPermissions: updateChildPermissions(childPermission),
        parentResourceType: resourceType,
        parentResourceValue: resourceValue,
      }));
    }
  }, []);
  const controlledFields = fields.map((field, index) => ({
    ...field,
    ...watchFields[index],
  }));
  useEffect(() => {
    controlledFields?.forEach(cf =>
      cf.childPermissions.forEach(cp => {
        cp.parentResourceType = cf.resourceType;
        cp.parentResourceValue = cf.resourceValue;
      }),
    );
  }, [controlledFields]);
  return (
    <div>
      {controlledFields?.map((el, idx) => (
        <PermissionsCard
          {...el}
          childPermissions={el.childPermissions || []}
          parentResourceType={el.parentResourceType}
          parentResourceValue={el.parentResourceValue}
          sx={sx}
          prefix={`${prefix}.${idx}`}
          key={el.id}
          onDelete={() => remove(idx)}
        />
      ))}
      <AddNewPermissionCard
        onClick={() => {
          append({
            childPermissions: [],
            resourceValue: '',
            trustLevel: null,
            resourceType: '',
            isAdminPermission: false,
            parentResourceType: '',
            parentResourceValue: '',
            create: false,
            read: false,
            update: false,
            delete: false,
          });
        }}
      />
    </div>
  );
};

export default PermissionsWrapper;
